import { useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { input, label, btnPrimary, btnGhost } from "./ui";
import { AlertTriangle } from "lucide-react";

const PEND_TEXT = "Estou ciente de que esta informação é obrigatória e será preenchida posteriormente.";

export const emptyPatientForm = {
  name: "", email: "", phone: "", notes: "", email_enabled: true,
  main_complaint: "", hypothesis: "", treatment_plan: "", medications: "",
  cpf: "", birth_date: "",
  address: { cep: "", street: "", number: "", complement: "", district: "", city: "", state: "" },
  emergency_contact: { name: "", relation: "", phone: "", phone_alt: "" },
  has_legal_guardian: false,
  legal_guardian: { name: "", relation: "", cpf: "", phone: "" },
  conditions: { session_price: null, modality: "", duration_min: 50, frequency: "", usual_day: "", usual_time: "", payment_method: "", payment_due: "", cancellation_policy: "" },
};

const getPath = (obj, path) => path.split(".").reduce((o, k) => (o || {})[k], obj) ?? "";
const setPath = (obj, path, value) => {
  const keys = path.split(".");
  const out = { ...obj };
  let cur = out;
  for (let i = 0; i < keys.length - 1; i++) { cur[keys[i]] = { ...(cur[keys[i]] || {}) }; cur = cur[keys[i]]; }
  cur[keys[keys.length - 1]] = value;
  return out;
};

function Field({ lbl, path, form, setForm, pend, setPend, required = true, type = "text", placeholder = "" }) {
  const value = getPath(form, path);
  const isEmpty = !String(value || "").trim();
  const tid = "pf-" + path.replace(/\./g, "-");
  const onChange = (v) => {
    setForm(setPath(form, path, v));
    if (String(v).trim() && pend[path]) {
      const p = { ...pend };
      delete p[path]; // campo preenchido remove a pendência automaticamente
      setPend(p);
    }
  };
  return (
    <div>
      <label className={label}>{lbl}{required && " *"}</label>
      <input type={type} className={input} value={value || ""} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} data-testid={tid} />
      {required && isEmpty && (
        <label className="flex items-start gap-2 mt-1.5 cursor-pointer select-none" data-testid={`${tid}-pend`}>
          <input type="checkbox" className="w-3.5 h-3.5 mt-0.5 accent-[#C87A5E]" checked={!!pend[path]}
            onChange={(e) => {
              const p = { ...pend };
              if (e.target.checked) p[path] = {}; else delete p[path]; // desmarcar nunca apaga o dado
              setPend(p);
            }} />
          <span className="font-body text-[11px] text-[#B2644D]">{PEND_TEXT}</span>
        </label>
      )}
    </div>
  );
}

const Section = ({ title, warn, children }) => (
  <div className={`rounded-xl border p-4 space-y-4 ${warn ? "border-[#C87A5E]/40 bg-[#C87A5E]/5" : "border-[#5B6E5B]/15"}`}>
    <p className={`font-body text-xs uppercase tracking-wider flex items-center gap-1.5 ${warn ? "text-[#B2644D]" : "text-[#5B6E5B]"}`}>
      {warn && <AlertTriangle className="w-3.5 h-3.5" />}{title}
    </p>
    {children}
  </div>
);

export default function PatientFormDialog({ open, onOpenChange, patient, onSaved }) {
  const editing = !!patient;
  const [form, setForm] = useState(emptyPatientForm);
  const [pend, setPend] = useState({});

  // carrega o paciente ao abrir (campos clínicos e condições são preservados no envio)
  const hydrate = () => {
    if (!patient) { setForm(emptyPatientForm); setPend({}); return; }
    setForm({
      ...emptyPatientForm, ...patient,
      address: { ...emptyPatientForm.address, ...(patient.address || {}) },
      emergency_contact: { ...emptyPatientForm.emergency_contact, ...(patient.emergency_contact || {}) },
      legal_guardian: { ...emptyPatientForm.legal_guardian, ...(patient.legal_guardian || {}) },
      conditions: { ...emptyPatientForm.conditions, ...(patient.conditions || {}) },
    });
    setPend(patient.pending_fields || {});
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error("Informe o nome completo.");
    try {
      const payload = { ...form, pending_fields: pend };
      if (editing) await api.put(`/patients/${patient.id}`, payload);
      else await api.post("/patients", payload);
      toast.success("Paciente salvo.");
      onOpenChange(false);
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao salvar. Revise os campos obrigatórios.");
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (v) hydrate(); onOpenChange(v); }}>
      <DialogContent className="bg-[#FDFBF7] max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl text-[#2C332A]">{editing ? "Editar paciente" : "Novo paciente"}</DialogTitle>
          <DialogDescription className="sr-only">Cadastro completo do paciente</DialogDescription>
        </DialogHeader>
        <form onSubmit={save} className="space-y-4" data-testid="patient-form">
          <Section title="Identificação pessoal">
            <Field lbl="Nome completo" path="name" form={form} setForm={setForm} pend={pend} setPend={setPend} required={false} />
            <div className="grid sm:grid-cols-2 gap-4">
              <Field lbl="CPF" path="cpf" form={form} setForm={setForm} pend={pend} setPend={setPend} placeholder="000.000.000-00" />
              <Field lbl="Data de nascimento" path="birth_date" type="date" form={form} setForm={setForm} pend={pend} setPend={setPend} />
              <Field lbl="Telefone/WhatsApp" path="phone" form={form} setForm={setForm} pend={pend} setPend={setPend} placeholder="(42) 90000-0000" />
              <Field lbl="E-mail" path="email" type="email" form={form} setForm={setForm} pend={pend} setPend={setPend} />
            </div>
          </Section>

          <Section title="Endereço">
            <div className="grid sm:grid-cols-3 gap-4">
              <Field lbl="CEP" path="address.cep" form={form} setForm={setForm} pend={pend} setPend={setPend} placeholder="00000-000" />
              <div className="sm:col-span-2"><Field lbl="Logradouro" path="address.street" form={form} setForm={setForm} pend={pend} setPend={setPend} /></div>
              <Field lbl="Número" path="address.number" form={form} setForm={setForm} pend={pend} setPend={setPend} />
              <div className="sm:col-span-2"><Field lbl="Complemento" path="address.complement" form={form} setForm={setForm} pend={pend} setPend={setPend} required={false} /></div>
              <Field lbl="Bairro" path="address.district" form={form} setForm={setForm} pend={pend} setPend={setPend} />
              <Field lbl="Cidade" path="address.city" form={form} setForm={setForm} pend={pend} setPend={setPend} />
              <Field lbl="Estado" path="address.state" form={form} setForm={setForm} pend={pend} setPend={setPend} placeholder="PR" />
            </div>
          </Section>

          <Section title="Contato de emergência — importante regularizar antes do primeiro atendimento" warn>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field lbl="Nome completo" path="emergency_contact.name" form={form} setForm={setForm} pend={pend} setPend={setPend} />
              <Field lbl="Parentesco/vínculo" path="emergency_contact.relation" form={form} setForm={setForm} pend={pend} setPend={setPend} placeholder="Mãe, esposo..." />
              <Field lbl="Telefone" path="emergency_contact.phone" form={form} setForm={setForm} pend={pend} setPend={setPend} />
              <Field lbl="Telefone alternativo" path="emergency_contact.phone_alt" form={form} setForm={setForm} pend={pend} setPend={setPend} required={false} />
            </div>
          </Section>

          <Section title="Responsável legal">
            <label className="flex items-center gap-3 cursor-pointer select-none" data-testid="pf-guardian-toggle">
              <input type="checkbox" className="w-4 h-4 accent-[#5B6E5B]" checked={form.has_legal_guardian}
                onChange={(e) => setForm({ ...form, has_legal_guardian: e.target.checked })} />
              <span className="font-body text-sm text-[#4A5D4E]">Paciente tem responsável legal (menor de idade ou representação)</span>
            </label>
            {form.has_legal_guardian && (
              <div className="grid sm:grid-cols-2 gap-4">
                <Field lbl="Nome completo" path="legal_guardian.name" form={form} setForm={setForm} pend={pend} setPend={setPend} />
                <Field lbl="Vínculo" path="legal_guardian.relation" form={form} setForm={setForm} pend={pend} setPend={setPend} placeholder="Mãe, pai, tutor..." />
                <Field lbl="Telefone" path="legal_guardian.phone" form={form} setForm={setForm} pend={pend} setPend={setPend} />
                <Field lbl="CPF" path="legal_guardian.cpf" form={form} setForm={setForm} pend={pend} setPend={setPend} required={false} />
              </div>
            )}
          </Section>

          <div><label className={label}>Observações administrativas</label>
            <textarea rows={2} className={`${input} resize-none`} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="pf-notes" /></div>
          <label className="flex items-center gap-3 cursor-pointer select-none" data-testid="pf-email-enabled">
            <input type="checkbox" checked={form.email_enabled} onChange={(e) => setForm({ ...form, email_enabled: e.target.checked })} className="w-4 h-4 accent-[#5B6E5B]" />
            <span className="font-body text-sm text-[#4A5D4E]">Enviar e-mails automáticos (confirmação e lembrete) para este paciente</span>
          </label>

          <div className="flex justify-end gap-3 pt-2">
            <button type="button" className={btnGhost} onClick={() => onOpenChange(false)}>Cancelar</button>
            <button type="submit" className={btnPrimary} data-testid="pf-save">Salvar</button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
